import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
} from "@mui/material";
import { Button, LoadingSpinner, ToastContainer } from "pepsico-ds";
import PropTypes from "prop-types";
import { useCallback, useState } from "react";
import { useStoreFileUpload } from "../../hooks/useStoreFileUpload";
import JsonFormsWrapper from "../../components/jsonForms/jsonFormsWrapper/JsonFormsWrapper";
import {
  storeFileUploadJsonSchema,
  storeFileUploadUISchema,
} from "./storeFileUploadSchema";
import "./storeManagement.scss";

const StoreFileUploadModal = ({ isOpen, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [modalToast, setModalToast] = useState([]);
  const { handleImportFileCall, handleDownloadSampleFile } = useStoreFileUpload();

  const setToast = useCallback((text, type, id) => {
    setModalToast([{ text, type, id }]);
  }, []);

  const onCloseModal = useCallback(() => {
    setFormData({});
    setIsSubmitting(false);
    setModalToast([]);
    onClose();
  }, [onClose]);

  const validateFormData = useCallback((data) => {
    const files = data?.storeFileData || [];
    if (files.length === 0) {
      return ["File is required"];
    }
    return [];
  }, []);

  const handleSubmit = useCallback(async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    const errors = validateFormData(formData);
    if (errors.length > 0) {
      setIsSubmitting(false);
      setModalToast(
        errors.map((error, index) => ({
          text: error,
          type: "error",
          id: `validation-error-${index}`,
        }))
      );
      return;
    }

    const files = formData?.storeFileData || [];

    try {
      const uploadResponse = await handleImportFileCall(files);
      if (!uploadResponse?.status) {
        setToast(uploadResponse?.message || "File upload failed", "error", 5);
        setIsSubmitting(false);
        return;
      }

      setIsSubmitting(false);
      setFormData({});

      if (onSuccess) {
        onSuccess();
      }
      onCloseModal();
    } catch (error) {
      setToast(
        error?.message || "An unexpected error occurred during file upload",
        "error",
        5
      );
      setIsSubmitting(false);
    }
  }, [
    isSubmitting,
    formData,
    validateFormData,
    handleImportFileCall,
    setToast,
    onSuccess,
    onCloseModal,
  ]);

  if (!isOpen) return null;

  return (
    <Dialog
      className="store-file-upload-modal width-md"
      open={isOpen}
      scroll="paper"
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Grid container justifyContent="space-between" direction="row">
          <Grid item xs={10}>
            <h2
              data-testid="store-file-upload-title"
              style={{ marginLeft: "18%", textAlign: "center" }}
            >
              Upload Stores
            </h2>
          </Grid>
          <Grid item xs={1} display="flex" justifyContent="flex-end">
            <CloseIcon
              onClick={onCloseModal}
              className="cursor-pointer"
              data-testid="store-file-upload-close"
            />
          </Grid>
        </Grid>
        <ToastContainer
          data={modalToast}
          removeToast={() => setModalToast([])}
          showActionIcon
        />
      </DialogTitle>
      <DialogContent sx={{ overflowX: "hidden" }}>
        <div className="my-5 store-upload-wrapper">
          <JsonFormsWrapper
            jsonschema={storeFileUploadJsonSchema}
            uischema={storeFileUploadUISchema}
            data={formData}
            onChange={(val) => {
              setIsSubmitting(false);
              setFormData(val);
            }}
          />
        </div>
        <Button
          size="small"
          variant="secondary"
          onClick={() => handleDownloadSampleFile("text/csv", "store_sample.csv")}
          className="download-sample-file"
        >
          Download sample file <DownloadIcon fontSize="small" />
        </Button>
      </DialogContent>
      <DialogActions className="mt-5 display-flex justify-content-end align-items-center">
        {isSubmitting && (
          <LoadingSpinner
            data-testid="upload-loading-spinner"
            size={{ height: "24px", width: "24px" }}
          />
        )}
        <Button variant="primary" disabled={isSubmitting} onClick={handleSubmit}>
          Upload
        </Button>
      </DialogActions>
    </Dialog>
  );
};

StoreFileUploadModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
};

export default StoreFileUploadModal;
